import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  InlineFeedback,
  Modal,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import useInviteToApply from '../../../hooks/useInviteToApply';
import { ACTION_TEXT } from '../../../utils/getApplicationActionText';
import useModal from '../../../hooks/useModal';
import TermsOfUse from '../../TermsOfUse';

type SendApplicationConfirmationModalProps = {
  conversationId: string;
  clientTimeZone: string;
  leadDisplayName: string;
  listingAlias: string;
};

export default function SendApplicationConfirmationModal({
  conversationId,
  clientTimeZone,
  leadDisplayName,
  listingAlias,
}: SendApplicationConfirmationModalProps) {
  const { actionText, handleInviteToApplyClick, helperText, isLoading, isSendingInvite } = useInviteToApply({
    conversationId,
    clientTimeZone,
    listingAlias,
    shouldShowApplicationUpsellModal: false,
  });

  const { clearModal } = useModal();
  const handleClose = () => clearModal();
  const confirmationActionText = actionText === ACTION_TEXT.RESEND_APPLICATION ? 'resent' : 'sent';

  const handleInviteToApplyClickWrapper = () => {
    return handleInviteToApplyClick();
  };

  return (
    <Modal
      open={true}
      onOpenChange={() => handleClose()}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      header={
        <Heading level={2} textStyle="heading-xs">
          {actionText}
        </Heading>
      }
      body={
        <>
          <Paragraph
            css={{ marginBottom: 'default' }}
          >{`An application invitation will be ${confirmationActionText} to ${leadDisplayName}.`}</Paragraph>
          {actionText && <TermsOfUse actionText={actionText} />}
        </>
      }
      footer={
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          <ButtonGroup aria-label="Modal actions" css={{ display: 'flex', justifyContent: 'flex-end' }}>
            <TextButton onClick={handleClose}>Cancel</TextButton>
            <Button
              tone={actionText === ACTION_TEXT.RESEND_APPLICATION ? 'neutral' : 'brand'}
              emphasis={actionText === ACTION_TEXT.RESEND_APPLICATION ? 'outlined' : 'filled'}
              // Disabled until the cross-surface getMessages fetch resolves, so the invite can't
              // fire with empty renter details (no cache yet) or before the cache exists to patch.
              disabled={isLoading || !actionText || isSendingInvite}
              onClick={handleInviteToApplyClickWrapper}
            >
              {isSendingInvite ? 'Sending...' : actionText}
            </Button>
          </ButtonGroup>

          {helperText.text && (
            <InlineFeedback
              tone={helperText.isFailure ? 'critical' : 'success'}
              css={{ marginLeft: 'auto', marginTop: 'default' }}
            >
              {helperText.text}
            </InlineFeedback>
          )}
        </Box>
      }
    />
  );
}
